/**
 * URL Query Parameters Utilities
 * 
 * Helper functions for handling URL query parameters.
 */

/**
 * Get a query parameter value from current URL.
 */
export function getQueryParam(key: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

/**
 * Get all query parameters as an object.
 */
export function getAllQueryParams(): Record<string, string> {
  if (typeof window === "undefined") {
    return {};
  }
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

/**
 * Build a query string from an object.
 */
export function buildQueryString(params: Record<string, string>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });
  return searchParams.toString();
}

/**
 * Preserve query parameters when building a URL.
 * Merges existing query params with additional params.
 */
export function preserveQueryParams(
  baseUrl: string,
  additionalParams?: Record<string, string>
): string {
  const existingParams = getAllQueryParams();
  const mergedParams = { ...existingParams, ...additionalParams };
  const queryString = buildQueryString(mergedParams);
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Update a single query parameter in the current URL.
 * Returns new URL without navigating.
 */
export function updateQueryParam(key: string, value: string | null): string {
  if (typeof window === "undefined") {
    return "";
  }
  const params = new URLSearchParams(window.location.search);
  if (value === null) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
  const queryString = params.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;
  return newUrl;
}

